import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Mail, Plus, Pencil, Trash2, Play, Bell, BellOff, Clock,
  Search, Check, AlertTriangle, History, RefreshCw, Settings2,
  ChevronDown, ChevronUp
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = [
  { key: 'monday', label: 'Lun' },
  { key: 'tuesday', label: 'Mar' },
  { key: 'wednesday', label: 'Mer' },
  { key: 'thursday', label: 'Jeu' },
  { key: 'friday', label: 'Ven' },
  { key: 'saturday', label: 'Sam' },
  { key: 'sunday', label: 'Dim' },
];

const DAYS_FR = {
  monday: 'Lundi', tuesday: 'Mardi', wednesday: 'Mercredi',
  thursday: 'Jeudi', friday: 'Vendredi', saturday: 'Samedi', sunday: 'Dimanche',
};

const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const slotKey = (siteId, day, time, type) => `${siteId}|${day}|${time}|${type}`;

const emptyRule = () => ({
  name: '',
  enabled: true,
  send_time: '09:00',
  days_of_week: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  selected_slots: [],
  content: { include_absent: true, include_present: false },
});

/** Minutes depuis minuit pour une heure HH:MM. */
function timeToMinutes(t) {
  if (!t) return null;
  const [h, m] = String(t).split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

/** Fin de fenêtre = heure créneau + marge after (minutes). */
function slotWindowEndMinutes(slotTime, marginAfter) {
  const base = timeToMinutes(slotTime);
  if (base === null) return null;
  return base + (marginAfter || 0);
}

/**
 * Éligible si l'heure d'envoi est strictement après la fin de fenêtre
 * (même règle que le backend is_slot_eligible_for_digest).
 */
function isSlotEligibleForSendTime(sendTime, slotTime, slotType, secteur) {
  const sendMin = timeToMinutes(sendTime);
  const margin =
    slotType === 'fermeture'
      ? (secteur.margin_after_close ?? 30)
      : (secteur.margin_after_open ?? 5);
  const endMin = slotWindowEndMinutes(slotTime, margin);
  if (sendMin === null || endMin === null) return false;
  return sendMin > endMin;
}

/** Créneaux d'un secteur éligibles pour l'heure d'envoi donnée. */
function getSecteurSlots(secteur, sendTime) {
  const result = [];
  const schedule = secteur.schedule || {};
  for (const day of DAYS_ORDER) {
    const daySched = schedule[day];
    if (!daySched || daySched.closed) continue;
    for (const slot of daySched.slots || []) {
      const time = slot.start || slot.time;
      if (!time) continue;
      const type = slot.type || 'ouverture';
      if (sendTime && !isSlotEligibleForSendTime(sendTime, time, type, secteur)) {
        continue;
      }
      result.push({
        site_id: secteur.id,
        day,
        time,
        type,
      });
    }
  }
  return result;
}

/** Retire de la sélection les créneaux devenus inéligibles. */
function pruneSelectedSlots(selected, secteursList, sendTime) {
  const byId = Object.fromEntries((secteursList || []).map((s) => [s.id, s]));
  return (selected || []).filter((sl) => {
    const secteur = byId[sl.site_id];
    if (!secteur) return false;
    return isSlotEligibleForSendTime(sendTime, sl.time, sl.type, secteur);
  });
}

export default function AdminEmailRecaps() {
  const navigate = useNavigate();
  const isDark = document.documentElement.classList.contains('dark');

  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [history, setHistory] = useState([]);
  const [secteurs, setSecteurs] = useState([]);
  const [searchSecteur, setSearchSecteur] = useState('');
  const [tab, setTab] = useState('rules');

  const [emailSettings, setEmailSettings] = useState({
    email_alerts_enabled: true,
    individual_scan_alerts_enabled: true,
    individual_absence_alerts_enabled: true,
  });
  const [savingSettings, setSavingSettings] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState(emptyRule());
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [savingRule, setSavingRule] = useState(false);
  const [expandedSecteurs, setExpandedSecteurs] = useState({});

  const token = () => localStorage.getItem('token');
  const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

  const inputStyle = {
    background: isDark ? '#374151' : '#ffffff',
    borderColor: isDark ? '#4b5563' : '#e5e7eb',
    color: isDark ? '#ffffff' : '#111827',
    colorScheme: isDark ? 'dark' : 'light',
  };

  useEffect(() => {
    if (!token()) {
      navigate('/');
      return;
    }
    const guard = async () => {
      try {
        const res = await axios.get(`${API}/admins/my-permissions`, { headers: authHeaders() });
        if (!res.data?.permissions?.can_manage_email_digests) {
          toast.error('Permission refusée');
          navigate('/admin/dashboard');
          return;
        }
        loadAll();
      } catch {
        navigate('/admin/dashboard');
      }
    };
    guard();
  }, [navigate]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [settingsRes, rulesRes, historyRes, secteursRes] = await Promise.all([
        axios.get(`${API}/email-digests/settings`, { headers: authHeaders() }),
        axios.get(`${API}/email-digests/rules`, { headers: authHeaders() }),
        axios.get(`${API}/email-digests/history`, { headers: authHeaders() }),
        axios.get(`${API}/secteurs-view`, { headers: authHeaders() }),
      ]);
      setEmailSettings(settingsRes.data);
      setRules(rulesRes.data);
      setHistory(historyRes.data);
      setSecteurs(secteursRes.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Permission refusée');
        navigate('/admin/dashboard');
      } else {
        toast.error('Erreur de chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (patch) => {
    setSavingSettings(true);
    try {
      const next = { ...emailSettings, ...patch };
      const res = await axios.put(`${API}/email-digests/settings`, next, { headers: authHeaders() });
      setEmailSettings(res.data);
      toast.success('Paramètres enregistrés');
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingSettings(false);
    }
  };

  const openCreate = () => {
    setEditingRule(null);
    setForm(emptyRule());
    setValidation(null);
    setSearchSecteur('');
    setExpandedSecteurs({});
    setShowModal(true);
  };

  const openEdit = (rule) => {
    const sendTime = rule.send_time || '09:00';
    const selected = pruneSelectedSlots(rule.selected_slots || [], secteurs, sendTime);
    setEditingRule(rule);
    setForm({
      name: rule.name,
      enabled: rule.enabled,
      send_time: sendTime,
      days_of_week: rule.days_of_week || [],
      selected_slots: selected,
      content: rule.content || { include_absent: true, include_present: false },
    });
    setValidation(null);
    setSearchSecteur('');
    // Auto-expand secteurs that have selections
    const expanded = {};
    selected.forEach((s) => { expanded[s.site_id] = true; });
    setExpandedSecteurs(expanded);
    setShowModal(true);
  };

  const runValidation = async (data) => {
    setValidating(true);
    try {
      const res = await axios.post(`${API}/email-digests/rules/validate`, data, { headers: authHeaders() });
      setValidation(res.data);
      return res.data;
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur de validation';
      setValidation({ valid: false, error: msg, eligible: [], blocked: [] });
      return null;
    } finally {
      setValidating(false);
    }
  };

  const saveRule = async () => {
    if (!form.name.trim()) {
      toast.error('Nom obligatoire');
      return;
    }
    if (!form.selected_slots.length) {
      toast.error('Sélectionnez au moins un créneau');
      return;
    }
    const v = await runValidation(form);
    if (!v?.valid) {
      toast.error(v?.error || 'Créneaux non valides pour cette heure');
      return;
    }
    setSavingRule(true);
    try {
      if (editingRule) {
        await axios.put(`${API}/email-digests/rules/${editingRule.id}`, form, { headers: authHeaders() });
        toast.success('Règle mise à jour');
      } else {
        await axios.post(`${API}/email-digests/rules`, form, { headers: authHeaders() });
        toast.success('Règle créée');
      }
      setShowModal(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSavingRule(false);
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Supprimer la règle « ${rule.name} » ?`)) return;
    try {
      await axios.delete(`${API}/email-digests/rules/${rule.id}`, { headers: authHeaders() });
      toast.success('Règle supprimée');
      loadAll();
    } catch {
      toast.error('Erreur');
    }
  };

  const testRule = async (rule) => {
    try {
      const res = await axios.post(`${API}/email-digests/rules/${rule.id}/test`, {}, { headers: authHeaders() });
      const n = res.data?.rows?.length || 0;
      const to = res.data?.test_recipient || res.data?.emails_sent?.[0] || 'team-comm@prysme.eu';
      if (res.data?.sent) {
        toast.success(`Test envoyé uniquement à ${to} (${n} ligne(s))`);
      } else {
        toast.error(res.data?.reason || 'Échec du test');
      }
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec du test');
    }
  };

  const toggleDay = (day) => {
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(day)
        ? f.days_of_week.filter((d) => d !== day)
        : [...f.days_of_week, day],
    }));
    setValidation(null);
  };

  const isSlotSelected = (siteId, day, time, type) =>
    form.selected_slots.some(
      (s) => s.site_id === siteId && s.day === day && s.time === time && s.type === type
    );

  const toggleSlot = (slot) => {
    setForm((f) => {
      const exists = f.selected_slots.some(
        (s) => s.site_id === slot.site_id && s.day === slot.day && s.time === slot.time && s.type === slot.type
      );
      return {
        ...f,
        selected_slots: exists
          ? f.selected_slots.filter(
              (s) => !(s.site_id === slot.site_id && s.day === slot.day && s.time === slot.time && s.type === slot.type)
            )
          : [...f.selected_slots, slot],
      };
    });
    setValidation(null);
  };

  const selectAllSlotsForSecteur = (secteur) => {
    const slots = getSecteurSlots(secteur, form.send_time);
    setForm((f) => {
      const without = f.selected_slots.filter((s) => s.site_id !== secteur.id);
      return { ...f, selected_slots: [...without, ...slots] };
    });
    setExpandedSecteurs((e) => ({ ...e, [secteur.id]: true }));
    setValidation(null);
  };

  const clearSlotsForSecteur = (secteurId) => {
    setForm((f) => ({
      ...f,
      selected_slots: f.selected_slots.filter((s) => s.site_id !== secteurId),
    }));
    setValidation(null);
  };

  const selectAllSlotsAllSecteurs = () => {
    const all = secteurs.flatMap((s) => getSecteurSlots(s, form.send_time));
    setForm((f) => ({ ...f, selected_slots: all }));
    setValidation(null);
  };

  const onSendTimeChange = (value) => {
    setForm((f) => ({
      ...f,
      send_time: value,
      selected_slots: pruneSelectedSlots(f.selected_slots, secteurs, value),
    }));
    setValidation(null);
  };

  const countSlotsForSecteur = (siteId) =>
    form.selected_slots.filter((s) => s.site_id === siteId).length;

  const uniqueSitesCount = new Set(form.selected_slots.map((s) => s.site_id)).size;

  const filteredSecteurs = secteurs.filter((s) =>
    (s.nom || s.name || '').toLowerCase().includes(searchSecteur.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5BA4CF]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 lg:p-8 w-full">
        {/* Header */}
        <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1
              className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Récapitulatifs e-mail
            </h1>
            <p className="text-sm md:text-base text-gray-600 dark:text-zinc-400 mt-1">
              Configurez des envois groupés pour réduire le nombre de notifications · {rules.length} règle{rules.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Button onClick={openCreate} className="bg-[#5BA4CF] hover:bg-[#4A93BE] text-white shrink-0">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle règle
          </Button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Colonne paramètres */}
          <div className="xl:col-span-4 space-y-6">
            <div className="glass-card rounded-xl p-6 shadow-sm">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-[#5BA4CF]" />
                Paramètres des notifications
              </h3>
              <div className="space-y-3">
                <ToggleRow
                  label="Notifications e-mail (master)"
                  desc="Si désactivé, aucun mail n'est envoyé."
                  checked={emailSettings.email_alerts_enabled}
                  disabled={savingSettings}
                  onChange={(v) => saveSettings({ email_alerts_enabled: v })}
                />
                <ToggleRow
                  label="Mails individuels — scans hors délai"
                  desc="Alerte immédiate à chaque écart horaire."
                  checked={emailSettings.individual_scan_alerts_enabled}
                  disabled={savingSettings || !emailSettings.email_alerts_enabled}
                  onChange={(v) => saveSettings({ individual_scan_alerts_enabled: v })}
                />
                <ToggleRow
                  label="Mails individuels — absences"
                  desc="Alertes quand aucun scan n'est détecté."
                  checked={emailSettings.individual_absence_alerts_enabled}
                  disabled={savingSettings || !emailSettings.email_alerts_enabled}
                  onChange={(v) => saveSettings({ individual_absence_alerts_enabled: v })}
                />
              </div>
            </div>

            <div className="glass-card rounded-xl p-6 shadow-sm">
              <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <History className="h-5 w-5 text-[#5BA4CF]" />
                Derniers envois
              </h3>
              {history.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-zinc-400">Aucun envoi pour le moment.</p>
              ) : (
                <ul className="space-y-2 max-h-64 overflow-y-auto">
                  {history.slice(0, 8).map((h) => (
                    <li
                      key={h.id}
                      className="text-sm p-2 rounded-lg bg-gray-50 dark:bg-zinc-800/50 flex justify-between gap-2"
                    >
                      <span className="text-gray-900 dark:text-white truncate">{h.rule_name}</span>
                      <span className="text-gray-500 dark:text-zinc-400 shrink-0 text-xs">
                        {h.date} · {h.absent_count} abs.
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {history.length > 0 && (
                <button
                  type="button"
                  onClick={() => setTab('history')}
                  className="mt-3 text-sm text-[#5BA4CF] hover:underline"
                >
                  Voir tout l'historique →
                </button>
              )}
            </div>
          </div>

          {/* Colonne principale */}
          <div className="xl:col-span-8 space-y-4">
            {/* Tabs */}
            <div className="flex gap-2 border-b border-gray-200 dark:border-zinc-700">
              <TabBtn active={tab === 'rules'} onClick={() => setTab('rules')} icon={Mail} label="Règles de récap" count={rules.length} />
              <TabBtn active={tab === 'history'} onClick={() => setTab('history')} icon={History} label="Historique complet" count={history.length} />
            </div>

            {tab === 'rules' && (
              <>
                {rules.length === 0 ? (
                  <div className="glass-card rounded-xl p-12 text-center shadow-sm">
                    <Mail className="w-12 h-12 text-gray-300 dark:text-zinc-600 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-zinc-400 mb-4">
                      Aucune règle configurée. Créez un récap pour regrouper vos alertes.
                    </p>
                    <Button onClick={openCreate} className="bg-[#5BA4CF] hover:bg-[#4A93BE] text-white">
                      <Plus className="w-4 h-4 mr-2" /> Créer une règle
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {rules.map((rule, index) => (
                      <motion.div
                        key={rule.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className="glass-card rounded-xl p-5 shadow-sm border border-transparent hover:border-[#5BA4CF]/30 transition-all"
                      >
                        <div className="flex items-start gap-3 mb-3">
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ background: '#5BA4CF20' }}
                          >
                            <Mail className="h-5 w-5 text-[#5BA4CF]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full shrink-0 ${rule.enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                              <h3 className="font-semibold text-gray-900 dark:text-white truncate">{rule.name}</h3>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-zinc-400 mt-1 flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5 shrink-0" />
                              {rule.send_time} · {rule.selected_slots?.length || 0} créneau(x)
                              {rule.selected_slots?.length > 0 && (
                                <> · {new Set((rule.selected_slots || []).map((s) => s.site_id)).size} secteur(s)</>
                              )}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {rule.content?.include_absent && (
                            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">Absents</span>
                          )}
                          {rule.content?.include_present && (
                            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">Présents</span>
                          )}
                          {(rule.selected_slots || []).some((s) => s.type === 'ouverture') && (
                            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">Ouvertures</span>
                          )}
                          {(rule.selected_slots || []).some((s) => s.type === 'fermeture') && (
                            <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">Fermetures</span>
                          )}
                        </div>

                        <div className="flex gap-2 flex-wrap pt-3 border-t border-gray-100 dark:border-zinc-700">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => testRule(rule)}
                            title="Envoie uniquement à team-comm@prysme.eu"
                            className="h-8 text-gray-800 dark:text-zinc-100 border-gray-300 dark:border-zinc-600"
                          >
                            <Play className="w-3.5 h-3.5 mr-1" /> Test
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openEdit(rule)} className="h-8 text-gray-800 dark:text-zinc-100 border-gray-300 dark:border-zinc-600">
                            <Pencil className="w-3.5 h-3.5 mr-1" /> Modifier
                          </Button>
                          <Button variant="outline" size="sm" className="h-8 text-red-500 hover:text-red-600 border-gray-300 dark:border-zinc-600" onClick={() => deleteRule(rule)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </>
            )}

            {tab === 'history' && (
              <div className="glass-card rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[600px]">
                    <thead>
                      <tr className="text-left text-gray-500 dark:text-zinc-400 border-b border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800/50">
                        <th className="p-4 font-medium">Date / Heure</th>
                        <th className="p-4 font-medium">Règle</th>
                        <th className="p-4 font-medium">Lignes</th>
                        <th className="p-4 font-medium">Absents</th>
                        <th className="p-4 font-medium">Présents</th>
                        <th className="p-4 font-medium">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="p-8 text-center text-gray-400">Aucun envoi enregistré</td>
                        </tr>
                      ) : (
                        history.map((h) => (
                          <tr key={h.id} className="border-b border-gray-100 dark:border-zinc-800 hover:bg-gray-50 dark:hover:bg-zinc-800/30">
                            <td className="p-4 text-gray-900 dark:text-white whitespace-nowrap">{h.date} {h.send_time}</td>
                            <td className="p-4 text-gray-900 dark:text-white">{h.rule_name}</td>
                            <td className="p-4 text-gray-900 dark:text-white">{h.rows_count}</td>
                            <td className="p-4 text-red-600 dark:text-red-400 font-medium">{h.absent_count}</td>
                            <td className="p-4 text-green-600 dark:text-green-400">{h.present_count}</td>
                            <td className="p-4">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                h.is_test
                                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                  : 'bg-[#5BA4CF]/10 text-[#5BA4CF]'
                              }`}>
                                {h.is_test ? 'Test' : 'Auto'}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal création / édition */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto text-gray-900 dark:text-white"
          style={{ background: isDark ? '#1f2937' : '#ffffff' }}
        >
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">
              {editingRule ? 'Modifier la règle' : 'Nouvelle règle de récap'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2 text-gray-900 dark:text-white">
            <div>
              <Label className="text-gray-700 dark:text-zinc-300">Nom</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex. Récap ouvertures matin"
                className="mt-1 h-11 text-gray-900 dark:text-white placeholder:text-gray-400"
                style={inputStyle}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 dark:text-zinc-300">Heure d'envoi (Paris)</Label>
                <Input
                  type="time"
                  value={form.send_time}
                  onChange={(e) => onSendTimeChange(e.target.value)}
                  className="mt-1 h-11 text-gray-900 dark:text-white [color-scheme:light] dark:[color-scheme:dark]"
                  style={inputStyle}
                />
                <p className="text-[11px] text-gray-500 dark:text-zinc-400 mt-1">
                  Seuls les créneaux déjà terminés (heure + marge) avant {form.send_time || '—'} sont proposés.
                </p>
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer p-3 rounded-lg bg-gray-50 dark:bg-zinc-800 w-full">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                    className="w-4 h-4 text-[#5BA4CF] rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-zinc-200 font-medium">Règle active</span>
                </label>
              </div>
            </div>

            <div>
              <Label className="text-gray-700 dark:text-zinc-300 mb-2 block">Jours</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS.map((d) => (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => toggleDay(d.key)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      form.days_of_week.includes(d.key)
                        ? 'bg-[#5BA4CF] text-white shadow-sm'
                        : 'bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-300 hover:bg-gray-200 dark:hover:bg-zinc-700'
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-gray-700 dark:text-zinc-300">
                  Secteurs & créneaux
                  <span className="ml-2 text-xs font-normal text-gray-500 dark:text-zinc-400">
                    ({form.selected_slots.length} créneau{form.selected_slots.length !== 1 ? 'x' : ''} · {uniqueSitesCount} secteur{uniqueSitesCount !== 1 ? 's' : ''})
                  </span>
                </Label>
                <button type="button" className="text-xs text-[#5BA4CF] font-medium hover:underline" onClick={selectAllSlotsAllSecteurs}>
                  Tout sélectionner
                </button>
              </div>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchSecteur}
                  onChange={(e) => setSearchSecteur(e.target.value)}
                  placeholder="Rechercher un secteur..."
                  className="pl-9 h-10 text-gray-900 dark:text-white placeholder:text-gray-400"
                  style={inputStyle}
                />
              </div>
              <div
                className="max-h-72 overflow-y-auto rounded-lg border divide-y divide-gray-200 dark:divide-zinc-600"
                style={{ borderColor: isDark ? '#4b5563' : '#e5e7eb', background: isDark ? '#374151' : '#f8f9fa' }}
              >
                {filteredSecteurs.map((s) => {
                  const slots = getSecteurSlots(s, form.send_time);
                  const selectedCount = countSlotsForSecteur(s.id);
                  const isExpanded = !!expandedSecteurs[s.id];
                  return (
                    <div key={s.id}>
                      <div className="flex items-center gap-2 p-2.5 hover:bg-white/50 dark:hover:bg-zinc-600/50">
                        <button
                          type="button"
                          className="flex-1 flex items-center gap-2 text-left min-w-0"
                          onClick={() => setExpandedSecteurs((e) => ({ ...e, [s.id]: !e[s.id] }))}
                        >
                          {isExpanded
                            ? <ChevronUp className="w-4 h-4 text-gray-400 shrink-0" />
                            : <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />}
                          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {s.nom || s.name}
                          </span>
                          <span className="shrink-0 text-[10px] text-gray-400 dark:text-zinc-500">
                            {slots.length} dispo
                          </span>
                          {selectedCount > 0 && (
                            <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[#5BA4CF]/20 text-[#5BA4CF]">
                              {selectedCount}
                            </span>
                          )}
                        </button>
                        {slots.length > 0 && (
                          <button
                            type="button"
                            className="text-[11px] text-[#5BA4CF] hover:underline shrink-0"
                            onClick={() => selectAllSlotsForSecteur(s)}
                          >
                            Tout
                          </button>
                        )}
                        {selectedCount > 0 && (
                          <button
                            type="button"
                            className="text-[11px] text-gray-500 dark:text-zinc-400 hover:underline shrink-0"
                            onClick={() => clearSlotsForSecteur(s.id)}
                          >
                            Aucun
                          </button>
                        )}
                      </div>
                      {isExpanded && (
                        <div className="px-3 pb-3 pt-1 space-y-2 bg-white/40 dark:bg-zinc-800/40">
                          {slots.length === 0 ? (
                            <p className="text-xs text-gray-500 dark:text-zinc-400 italic px-1">
                              Aucun créneau terminé avant {form.send_time} (heure + marge after).
                            </p>
                          ) : (
                            DAYS_ORDER.map((day) => {
                              const daySlots = slots.filter((sl) => sl.day === day);
                              if (!daySlots.length) return null;
                              return (
                                <div key={day}>
                                  <p className="text-[11px] font-semibold text-gray-500 dark:text-zinc-400 mb-1 px-1">
                                    {DAYS_FR[day]}
                                  </p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {daySlots.map((sl) => {
                                      const checked = isSlotSelected(sl.site_id, sl.day, sl.time, sl.type);
                                      return (
                                        <button
                                          key={slotKey(sl.site_id, sl.day, sl.time, sl.type)}
                                          type="button"
                                          onClick={() => toggleSlot(sl)}
                                          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                                            checked
                                              ? sl.type === 'ouverture'
                                                ? 'bg-green-500 text-white'
                                                : 'bg-orange-500 text-white'
                                              : 'bg-gray-200 dark:bg-zinc-700 text-gray-700 dark:text-zinc-200 hover:bg-gray-300 dark:hover:bg-zinc-600'
                                          }`}
                                        >
                                          {sl.type === 'ouverture' ? 'Ouverture' : 'Fermeture'} {sl.time}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="p-4 rounded-lg bg-gray-50 dark:bg-zinc-800">
              <Label className="text-gray-700 dark:text-zinc-300 mb-3 block">Contenu du mail</Label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer text-gray-800 dark:text-zinc-100">
                  <input
                    type="checkbox"
                    checked={form.content.include_absent}
                    onChange={(e) => setForm({ ...form, content: { ...form.content, include_absent: e.target.checked } })}
                    className="w-4 h-4 text-[#5BA4CF] rounded"
                  />
                  Absents
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer text-gray-800 dark:text-zinc-100">
                  <input
                    type="checkbox"
                    checked={form.content.include_present}
                    onChange={(e) => setForm({ ...form, content: { ...form.content, include_present: e.target.checked } })}
                    className="w-4 h-4 text-[#5BA4CF] rounded"
                  />
                  Présents (scannés)
                </label>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              disabled={validating}
              onClick={() => runValidation(form)}
              className="text-gray-800 dark:text-zinc-100 border-gray-300 dark:border-zinc-600 hover:bg-gray-100 dark:hover:bg-zinc-700"
            >
              {validating ? (
                <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> Vérification...</>
              ) : (
                'Vérifier les créneaux'
              )}
            </Button>

            {validation && (
              <div className={`rounded-lg p-4 text-sm ${
                validation.valid
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
              }`}>
                {validation.valid ? (
                  <p className="flex items-center gap-2 font-medium">
                    <Check className="w-4 h-4" /> {validation.eligible?.length || 0} créneau(x) éligible(s)
                  </p>
                ) : (
                  <p className="flex items-start gap-2 font-medium">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {validation.error}
                  </p>
                )}
                {validation.blocked?.length > 0 && (
                  <ul className="mt-2 text-xs space-y-1 opacity-90 pl-6 list-disc">
                    {validation.blocked.slice(0, 5).map((b, i) => (
                      <li key={i}>{b.site_name} — {b.slot_type} {b.slot_time} (fin {b.window_end})</li>
                    ))}
                    {validation.blocked.length > 5 && <li>… et {validation.blocked.length - 5} autre(s)</li>}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-zinc-700">
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              className="text-gray-800 dark:text-zinc-100 border-gray-300 dark:border-zinc-600 hover:bg-gray-100 dark:hover:bg-zinc-700"
            >
              Annuler
            </Button>
            <Button onClick={saveRule} disabled={savingRule} className="bg-[#5BA4CF] hover:bg-[#4A93BE] text-white">
              {savingRule ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function ToggleRow({ label, desc, checked, disabled, onChange }) {
  return (
    <label
      className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
        checked ? 'bg-[#5BA4CF]/10' : 'bg-gray-50 dark:bg-zinc-800/50'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#5BA4CF]/5'}`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={() => !disabled && onChange(!checked)}
        className="mt-1 w-4 h-4 text-[#5BA4CF] rounded border-gray-300 focus:ring-[#5BA4CF]"
      />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900 dark:text-white text-sm">{label}</p>
        <p className="text-xs text-gray-500 dark:text-zinc-400 mt-0.5">{desc}</p>
      </div>
      <div className="shrink-0 pt-0.5">
        {checked ? <Bell className="w-4 h-4 text-green-500" /> : <BellOff className="w-4 h-4 text-gray-400" />}
      </div>
    </label>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition ${
        active
          ? 'border-[#5BA4CF] text-[#5BA4CF]'
          : 'border-transparent text-gray-500 dark:text-zinc-400 hover:text-gray-700 dark:hover:text-zinc-300'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
      {count !== undefined && (
        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
          active ? 'bg-[#5BA4CF]/20 text-[#5BA4CF]' : 'bg-gray-200 dark:bg-zinc-700 text-gray-500'
        }`}>
          {count}
        </span>
      )}
    </button>
  );
}
